import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  card: "var(--pr-card)", ink: "var(--pr-ink)", ink2: "var(--pr-ink2)", muted: "var(--pr-muted)",
  line: "var(--pr-line)", gold: "var(--pr-gold)", teal: "var(--pr-teal)", bg: "var(--pr-bg)",
};

const PULSES = {
  post_setup: {
    emoji: "🔧",
    title: "Before you start your first relay, did anything feel confusing or broken?",
    sub: "30 seconds. It helps us catch onboarding friction early.",
    chips: [
      { k: "setup_worked", q: "Did setup work as expected?", opts: ["Yes", "Not quite"] },
      { k: "sheet_opened", q: "Did your sheet open correctly?", opts: ["Yes", "No", "No sheet yet"] },
      { k: "caddy_made_sense", q: "Did the Caddy make sense when you first opened it?", opts: ["Yes", "Mostly", "Not really"] },
    ],
    texts: [
      { k: "confusing", q: "Was anything confusing?" },
      { k: "expected_missing", q: "Anything you expected to happen that didn't?" },
    ],
  },
  relay1: {
    emoji: "🏁",
    title: "You finished your first relay. How did the rhythm feel?",
    sub: "Quick pulse, skip anything you like.",
    chips: [
      { k: "understood_target", q: "Did you understand your weekly target?", opts: ["Yes", "Sort of", "No"] },
      { k: "daily_target_helped", q: "Did the daily target help you decide what to focus on?", opts: ["Yes", "Sort of", "No"] },
      { k: "logging_felt", q: "Logging earnings felt…", opts: ["Easy", "Fine", "Annoying"] },
      { k: "payday_delay_sense", q: "Did the payday rhythm make sense?", opts: ["Yes", "Sort of", "No"] },
      { k: "closeout_sense", q: "Did closing out allocations make sense?", opts: ["Yes", "Sort of", "No"] },
    ],
    texts: [{ k: "broken_unclear", q: "Anything feel broken, unclear, or stressful?" }],
  },
};

export const FeedbackPulseCard = ({ refreshKey }) => {
  const [due, setDue] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/caddy/feedback/prompt`, { credentials: "include" });
        if (!res.ok) return;
        const d = await res.json();
        setDue(d.due);
      } catch { /* stay hidden */ }
    })();
  }, [refreshKey]);

  const post = async (skipped) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/caddy/feedback`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind: due, answers: skipped ? {} : answers, skipped }),
      });
      if (!res.ok) { toast.error("Couldn't save, try again"); return; }
      if (!skipped) {
        setDone(true);
        toast.success("Thank you. This shapes what we build 🏁");
        setTimeout(() => setDue(null), 2500);
      } else {
        setDue(null);
      }
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!due) return null;

  // Four Relay Challenge, invite to the full survey page
  if (due === "four_relay") {
    return (
      <div className="rounded-2xl p-4 border-2" style={{ background: "var(--pr-tint-gold)", borderColor: COLOR.gold }} data-testid="feedback-four-relay-invite">
        <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: "var(--pr-accent-gold-text)" }}>
          🏆 Four Relay Challenge complete
        </p>
        <p className="text-sm font-bold mb-1" style={{ color: COLOR.ink }}>
          You completed the Four Relay Challenge. We'd love your beta feedback.
        </p>
        <p className="text-xs mb-3" style={{ color: COLOR.ink2 }}>
          Four relays is enough to feel the rhythm, tell us what worked, what broke, and what's worth paying for.
        </p>
        <div className="flex gap-2">
          <Link
            to="/app/beta-feedback"
            className="flex-1 py-2.5 rounded-full font-bold text-sm text-center"
            style={{ background: "var(--pr-gold)", color: "var(--pr-on-gold)" }}
            data-testid="feedback-survey-link"
          >
            Take the 3-min survey →
          </Link>
          <button
            onClick={() => post(true)}
            disabled={busy}
            className="px-4 py-2.5 rounded-full text-xs underline"
            style={{ color: COLOR.muted }}
            data-testid="feedback-skip-btn"
          >
            Not now
          </button>
        </div>
      </div>
    );
  }

  const cfg = PULSES[due];
  if (!cfg) return null;

  if (done) {
    return (
      <div className="rounded-2xl p-4 border text-center" style={{ background: "var(--pr-card)", borderColor: COLOR.line }} data-testid="feedback-thanks">
        <p className="text-sm font-bold" style={{ color: COLOR.teal }}>Thank you 🏁 Your feedback shapes the build.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl p-4 border" style={{ background: "var(--pr-card)", borderColor: COLOR.line }} data-testid={`feedback-pulse-${due}`}>
      <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: COLOR.teal }}>
        {cfg.emoji} Beta pulse
      </p>
      <p className="text-sm font-bold mb-0.5" style={{ color: COLOR.ink }} data-testid="feedback-pulse-title">{cfg.title}</p>
      <p className="text-[11px] mb-3" style={{ color: COLOR.muted }}>{cfg.sub}</p>

      <div className="space-y-2.5 mb-3">
        {cfg.chips.map((c) => (
          <div key={c.k}>
            <p className="text-xs mb-1" style={{ color: COLOR.ink2 }}>{c.q}</p>
            <div className="flex flex-wrap gap-1.5">
              {c.opts.map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setAnswers((p) => ({ ...p, [c.k]: o }))}
                  className="text-[11px] px-3 py-1.5 rounded-full border font-semibold"
                  style={answers[c.k] === o
                    ? { background: "var(--pr-ink-surface)", color: "#fff", borderColor: COLOR.ink }
                    : { background: "var(--pr-card)", color: COLOR.ink2, borderColor: COLOR.line }}
                  data-testid={`pulse-${c.k}-${o.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {o}
                </button>
              ))}
            </div>
          </div>
        ))}
        {cfg.texts.map((t) => (
          <div key={t.k}>
            <p className="text-xs mb-1" style={{ color: COLOR.ink2 }}>{t.q}</p>
            <textarea
              rows={2}
              value={answers[t.k] || ""}
              onChange={(e) => setAnswers((p) => ({ ...p, [t.k]: e.target.value }))}
              placeholder="Optional…"
              className="w-full px-3 py-2 rounded-xl border text-xs focus:outline-none"
              style={{ borderColor: COLOR.line, background: "var(--pr-bg)", color: COLOR.ink }}
              data-testid={`pulse-${t.k}-input`}
            />
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => post(false)}
          disabled={busy || Object.keys(answers).length === 0}
          className="flex-1 py-2.5 rounded-full font-bold text-sm disabled:opacity-40"
          style={{ background: "var(--pr-teal-surface)", color: "#fff" }}
          data-testid="feedback-submit-btn"
        >
          {busy ? "Sending…" : "Send it →"}
        </button>
        <button
          onClick={() => post(true)}
          disabled={busy}
          className="px-4 py-2.5 rounded-full text-xs underline"
          style={{ color: COLOR.muted }}
          data-testid="feedback-skip-btn"
        >
          Skip
        </button>
      </div>
    </div>
  );
};
